const BaseLayer = require("./baselayer");
const DefaultRenderer = require("./renderers/defaultrenderer");
const SearchParameters = require("../searchparameters").SearchParameters;
const network = require("../network");
const maps = require("../maps");

/**
 * @typedef {Object} TiledOverlayOptions
 * @property {woosmap.search.SearchParameters} [searchParameters]
 * @property {woosmap.search.SearchQuery} [searchQuery] Deprecated - use `searchParameters`
 * @property {PolylineFilter} [polylineFilter] Deprecated - use `searchParameters`
 * @property {Number} [minZoom]
 */

/**
 * TiledDataOverlay
 * Uses marker vector tiles and displays it using a renderer.
 * @class
 * @extends woosmap.layers.BaseLayer
 * @memberOf woosmap.layers
 */
class TiledDataOverlay extends BaseLayer {
    /**
     * TiledDataOverlay.
     * @constructor
     * @param {TiledOverlayOptions} [options]
     * @since 1.1.0
     */
    constructor(options) {
        super(options);

        this._url = maps.utils.overlays.getJsonTilesUrlTemplate();

        this._cache = {};
        this._idleListener = null;
        this._tileSize = 256;
        this._options.maxZoom = 24;
        this._options.minZoom = this._options.minZoom || 0;

        if (this._options.searchParameters) {
            this._searchParameters = this._options.searchParameters;
        } else {
            this._searchParameters = new SearchParameters();

            if (this._options.searchQuery) {
                this._searchParameters.searchQuery = this._options.searchQuery;
            }
            if (this._options.polylineFilter) {
                this._searchParameters.polylineFilter = this._options.polylineFilter;
            }
        }
    }

    /**
     * Clears the overlay data and cache.
     * @since 1.1.2
     */
    clear() {
        super.clear();
        this._cache = {};
    }

    _default_renderer(options) {
        return new DefaultRenderer(options);
    }

    /**
     * Set the map instance
     * @param {google.maps.Map} map
     */
    setMap(map) {
        super.setMap(map);
        if (map !== null) this.onAdd();
        else this.onRemove();
    }

    _refreshMap() {
        const map = this.getMap();
        if (map) {
            this.setMap(null);
            this.setMap(map);
        }
    }

    /**
     * Sets the search parameters used to filter the displayed data.
     * @param {woosmap.search.SearchParameters} searchParameters
     */
    setSearchParameters(searchParameters) {
        this._searchParameters = searchParameters;
        this.clear();
        this._refreshMap();
    }

    /**
     * Sets the searchQuery used to filter the displayed data.
     * @param {woosmap.search.SearchQuery}searchQuery the searchQuery.
     * @deprecated
     */
    setSearchQuery(searchQuery) {
        this._searchParameters.searchQuery = searchQuery;
        this.clear();
        this._refreshMap();
    }

    /**
     * Sets the polylineFilter used to filter the displayed data.
     * @param {PolylineFilter}polylineFilter
     * @deprecated
     */
    setPolylineFilter(polylineFilter) {
        this._searchParameters.polylineFilter = polylineFilter;
        this.clear();
        this._refreshMap();
    }

    /**
     * @override
     */
    onAdd() {
        this._zoomChangedListener = maps.events.addListener(
            this._map,
            "zoom_changed",
            this._updateVisibility.bind(this),
        );

        this._idleListener = maps.events.addListener(this._map, "idle", this._update.bind(this));
        this._update();
    }

    /**
     * @override
     */
    onRemove() {
        if (this._idleListener) {
            maps.events.removeListener(this._map, this._idleListener);
            this._idleListener = null;
        }

        if (this._zoomChangedListener) {
            maps.events.removeListener(this._map, this._zoomChangedListener);
            this._zoomChangedListener = null;
        }
    }

    /**
     * Called when a tile has finished loading
     * @param {GeoJSON} data
     * @abstract
     */
    tileDidLoad(data) {
        this._renderer.tileDidLoad(data);
    }

    /**
     * Called when all the tiles requested from the api server were fetched.
     * @abstract
     */
    tilesDidLoad() {
        this._renderer.tilesDidLoad();
    }

    /**
     * Updates layer visibility based on minZoom and maxZoom.
     * @returns {boolean}
     * @private
     */
    _updateVisibility() {
        const map = this.getMap();
        if (map) {
            const zoom = map.getZoom();

            if (zoom > this._options.maxZoom || zoom < this._options.minZoom) {
                this.setHidden(true);
                return true;
            }
            this.setHidden(false);
            return false;
        }
        return true;
    }

    /**
     * Updates layer data and visibility.
     * @private
     */
    _update() {
        const hidden = this._updateVisibility();
        if (!hidden) {
            const zoom = this.getMap().getZoom();

            const mapBounds = this.getMap().getBounds();
            if (mapBounds !== undefined) {
                const mB_NE = mapBounds.getNorthEast();
                const mB_SW = mapBounds.getSouthWest();

                // Get tile coordinates
                const neTilePoint = maps.coordinates.getTileCoordinates(this.getMap(), mB_NE);
                const swTilePoint = maps.coordinates.getTileCoordinates(this.getMap(), mB_SW);

                // Load all required ones
                const requests = this._loadTiles(swTilePoint, neTilePoint, zoom);

                Promise.all(requests).then(() => {
                    this.tilesDidLoad();
                });
            }
        }
    }

    /**
     * Returns the tileUrl containing searchQuery
     * Normalizes the tile coordinates for y-axis repeated worlds.
     * @param {Number} zoom the current zoomLevel.
     * @param {Number} x tile x-axis coordinate.
     * @param {Number} y tile y-axis coordinate.
     * @returns {string|null}
     * @private
     */
    _getTileUrl(zoom, x, y) {
        const searchQueryFragment = this._searchParameters.buildUrl();

        const coordinates = this._normalizeCoordinate({ x: x, y: y }, zoom);

        if (coordinates) {
            return (
                this._url
                    .replace(/\$Z/g, Math.floor(zoom))
                    .replace(/\$X/g, coordinates.x)
                    .replace(/\$Y/g, coordinates.y) + searchQueryFragment
            );
        }
        return null;
    }

    /**
     * Mark Tile identified by zoom x, y, z as cached.
     * @param {Number} zoom
     * @param {Number} x
     * @param {Number} y
     * @param {GeoJSON} data the GeoJson corresponding to the tile
     * @private
     */
    _cache_tile_data(zoom, x, y, data) {
        const key = `${zoom}_${x}_${y}`;
        this._cache[key] = data;
    }

    /**
     * Checks that tile data was not previously loaded by parent.
     * @param x
     * @param y
     * @param z
     * @returns {boolean}
     * @private
     */
    _hasBeenLoadedByParent(x, y, z) {
        let locZ = z;
        let locX = x;
        let locY = y;
        while (locZ >= this._options.minZoom) {
            if (this._cache.hasOwnProperty(`${locZ}_${locX}_${locY}`)) {
                return true;
            }

            locZ--;

            locX = Math.floor(locX / 2);
            locY = Math.floor(locY / 2);
        }

        return false;
    }

    /**
     * Loads the tiles needed for the visible viewport.
     * @param swTilePoint
     * @param neTilePoint
     * @param zoom
     * @returns {Array.<Promise>} of promises.
     * @private
     */
    _loadTiles(swTilePoint, neTilePoint, zoom) {
        const requests = [];

        const width = Math.abs(swTilePoint.x - neTilePoint.x);
        const height = Math.abs(swTilePoint.y - neTilePoint.y);
        const x = swTilePoint.x;
        const y = swTilePoint.y;

        for (let dx = 0; dx <= width; dx++) {
            for (let dy = 0; dy <= height; dy++) {
                const tx = x + dx;
                const ty = y - dy;

                const key = `${zoom}_${tx}_${ty}`;

                if (!this._hasBeenLoadedByParent(tx, ty, zoom)) {
                    this._cache[key] = null;
                    requests.push(this._loadTile(zoom, tx, ty));
                }
            }
        }
        return requests;
    }

    /**
     * Loads a GeoJSON tile from the server using an XHR.
     * @param zoom
     * @param x
     * @param y
     * @returns {*}
     * @private
     */
    _loadTile(zoom, x, y) {
        return network
            .fetch(this._getTileUrl(zoom, x, y))
            .then((response) => {
                return response.json();
            })
            .then((data) => {
                this._cache_tile_data(zoom, x, y, data);
                this.tileDidLoad(data);
            });
    }

    /**
     * Normalize tile coordinates when using a x-axis repeated world.
     * @param coord
     * @param zoom
     * @returns {*}
     * @private
     */
    _normalizeCoordinate(coord, zoom) {
        if (window.BMap) {
            return coord;
        }
        const y = coord.y;
        let x = coord.x;

        // tile range in one direction range is dependent on zoom level
        // 0 = 1 tile, 1 = 2 tiles, 2 = 4 tiles, 3 = 8 tiles, etc
        const tileRange = 1 << zoom;

        // don't repeat across y-axis (vertically)
        if (y < 0 || y >= tileRange) {
            return null;
        }

        // repeat across x-axis
        if (x < 0 || x >= tileRange) {
            x = ((x % tileRange) + tileRange) % tileRange;
        }

        return { x: x, y: y };
    }
}

module.exports = TiledDataOverlay;
